/**
 * MultiSelectDropdown — a checkbox popover filter control. A button shows the
 * label plus a "(n)" summary when any option is selected; clicking opens a
 * popover of checkboxes (multiple selection) with a Select-all / Clear
 * affordance. Value is a Set<string>; an empty set means "no filter" (all
 * pass). Closes on outside-click / Esc, and is keyboard-navigable.
 *
 * Options may be plain strings or {value, label} pairs so callers can localize
 * the display label while filtering on the raw value.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale } from "@hooks/useLocale";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectDropdownProps {
  /** Uppercase field label shown above/on the trigger button. */
  label: string;
  /** Selectable options — strings are used as both value and label. */
  options: readonly (string | MultiSelectOption)[];
  /** Currently-selected values. Empty = no filter. */
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

function normalize(opt: string | MultiSelectOption): MultiSelectOption {
  return typeof opt === "string" ? { value: opt, label: opt } : opt;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
}: MultiSelectDropdownProps) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  const opts = useMemo(() => options.map(normalize), [options]);
  const count = selected.size;

  // Close on outside-click / Esc while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const selectAll = () => onChange(new Set(opts.map((o) => o.value)));
  const clear = () => onChange(new Set());

  const summary = count > 0 ? `${label} (${count})` : label;

  return (
    <div className="field msdd" ref={rootRef}>
      <span className="msdd-label">{label}</span>
      <button
        type="button"
        className={`msdd-trigger${count > 0 ? " has-selection" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={summary}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="msdd-trigger-text">{summary}</span>
        <span className="msdd-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="msdd-panel" role="listbox" aria-multiselectable="true" id={panelId}>
          <div className="msdd-actions">
            <button type="button" className="msdd-linkbtn" onClick={selectAll}>
              {t("filter.selectAll")}
            </button>
            <button
              type="button"
              className="msdd-linkbtn"
              onClick={clear}
              disabled={count === 0}
            >
              {t("filter.clear")}
            </button>
          </div>
          <ul className="msdd-list">
            {opts.map((o) => {
              const checked = selected.has(o.value);
              return (
                <li key={o.value} role="option" aria-selected={checked}>
                  <label className="msdd-item">
                    <input
                      type="checkbox"
                      className="brand-check"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                    />
                    <span className="msdd-item-text">{o.label}</span>
                  </label>
                </li>
              );
            })}
            {opts.length === 0 && <li className="msdd-empty">{t("filter.noOptions")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
